import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConsultationRequestAttachmentType } from '@enums/consultation-request-attachment-type';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { ConsultationRequestRequest } from '@models/consultation-request/consultation-request-request';
import { IndexedDbItem } from '@models/indexed-db-item';
import { OrderTest } from '@models/order-test';
import { Recharge } from '@models/recharge';
import { PaymentRechargeBraintreeStoreRequest } from '@models/requests/payment-recharge-briantree-store-request.model';
import { ConsultationResponse } from '@models/responses/consultation-response.model';
import { UpsellBatchStoreResponse } from '@models/responses/upsell-batch-store-response.model';
import { Test } from '@models/test';
import { NavigationService } from '@services/navigation.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { RequestRetryService } from '@services/request-retry.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { catchError, concatMap, forkJoin, from, map, Observable, of, switchMap, throwError } from 'rxjs';

import { ConsultationRequestService } from './consultation-request.service';
import { ConsultationStorageService } from './consultation-storage.service';
import { ErrorLoggingService } from './error-logging.service';
import { IndexedDbService } from './indexed-db.service';
import { OrderService } from './order.service';
import { StorageService } from './storage.service';
import { TreatmentConsultationQuestionnaireService } from './treatment-consultation-questionnaire.service';

@Injectable({
  providedIn: 'root',
})
export class TreatmentConsultationSubmissionService {
  public giftCardApplied = 0;
  public giftCardCode: string;

  private errorLoggingService = inject(ErrorLoggingService);
  private consultationRequestService = inject(ConsultationRequestService);
  private orderService = inject(OrderService);
  private storage = inject(StorageService);
  private consultationStorageService = inject(ConsultationStorageService);
  private indexedDbService = inject(IndexedDbService);
  private treatmentConsultationQuestionnaireService = inject(TreatmentConsultationQuestionnaireService);
  private orderPaymentService = inject(OrderPaymentService);
  private navigationService = inject(NavigationService);
  private treatmentTypeService = inject(TreatmentTypeService);
  private requestRetryService = inject(RequestRetryService);

  /**
   * Determine if a second payment is necessary by checking if the gift card applied (if there is one)
   * is enough to pay for the entire order.
   */
  public get isSecondaryPaymentNeeded(): boolean {
    return this.giftCardApplied && this.giftCardApplied < this.getSubmitTotal();
  }

  /**
   * Applies the current gift card to the cart.
   *
   * @param balance gift card balance
   */
  public applyGiftCard(balance: number): void {
    const total = this.getSubmitTotal();
    this.giftCardApplied = balance ? (total < balance * 100 ? total : balance * 100) : 0;
  }

  /**
   * Creates an observable for storing a single attachment.
   *
   * @param {Object} attachment the attachment object containing the file and its type
   *
   * @returns {Observable<any>} an observable that completes when the attachment is stored
   */
  public createAttachmentObservable(attachment: {
    file: File;
    type: ConsultationRequestAttachmentType;
  }): Observable<any> {
    return this.consultationRequestService.storeAttachment(
      this.consultationStorageService.consultationId,
      attachment.file,
      attachment.type,
    );
  }

  /**
   * Captures consultation request exceptions.
   *
   * @param {Error} error the error to capture
   *
   * @returns {Observable<never>} an observable that emits the error
   */
  public captureConsultationRequestException(error?: Error): Observable<never> {
    let customError = new Error('Failed when finishing the consultation request for an order.');

    if (error) {
      customError.stack = error.stack;
    }

    this.errorLoggingService.logError({
      message: customError.message,
      transactionId: this.consultationStorageService.transactionId ?? 'transactionId not found',
      hash: this.consultationStorageService.hash ?? 'hash not found',
      consultationId: this.consultationStorageService.consultationId ?? 'consultationId not found',
      ...{ context_info: error },
    });

    return throwError(() => error);
  }

  /**
   * Submits the treatment consultation request.
   *
   * This method orchestrates the submission process by:
   *  1. Submitting upsells to the order.
   *  2. Uploading attachments (ID card and other questionnaire files).
   *  3. Storing the partner email if applicable.
   *  4. Updating the preferred treatment on the order, if changed.
   *  5. Updating the customer address on the order.
   *  6. Updating the consultation with all necessary details.
   *
   * It handles errors at each step, logging them and attempting to continue the flow where appropriate.
   * The final consultation type and schedule once ID are stored in the service upon successful update.
   *
   * @param {object} [options] optional submission options
   *
   * @returns {Observable<ConsultationResponse>} An observable of the ConsultationResponse if successful.
   */
  public submit(options: { skipUpsellSubmission?: boolean } = {}): Observable<ConsultationResponse> {
    const handleSubmissionError = (error: any) => this.handleSubmissionError(error);
    const storeUpsellsRequest = options.skipUpsellSubmission ? of(null) : this.storeUpsells();

    return storeUpsellsRequest.pipe(
      concatMap(() => this.uploadAttachments().pipe(catchError(handleSubmissionError))),
      concatMap(() => this.storePartnerEmail().pipe(catchError(handleSubmissionError))),
      concatMap(() => this.updatePreferredTreatment().pipe(catchError(handleSubmissionError))),
      concatMap(() => this.updateCustomerAddress().pipe(catchError(handleSubmissionError))),
      concatMap(() => this.updateLab().pipe(catchError(handleSubmissionError))),
      concatMap(() => this.scheduleInHomeCollectionAppointment().pipe(catchError(handleSubmissionError))),
      concatMap(() =>
        this.updateConsultation().pipe(
          catchError((error) => this.captureConsultationRequestException(error)),
          catchError((error) => {
            this.navigationService.navigateToConsultationRequestSubmissionErrorPage();

            return throwError(() => error);
          }),
        ),
      ),
    );
  }

  /**
   * Creates payment request from recharge information.
   *
   * @param {Recharge} recharge the recharge object
   *
   * @returns {PaymentRechargeBraintreeStoreRequest} the payment request object
   */
  public createPaymentRequestFromRecharge(recharge: Recharge): PaymentRechargeBraintreeStoreRequest {
    const orderUpsells = this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice();
    const upsellChargeAmount = orderUpsells.reduce((acc, upsell) => acc + upsell.upsell_price, 0);
    const previousBalance = Math.max(this.consultationStorageService.remainingBalance, 0);

    return new PaymentRechargeBraintreeStoreRequest(
      this.consultationStorageService.transactionId,
      upsellChargeAmount + previousBalance + this.orderService.getInHomeCollectionTotal(),
      recharge.vault_id,
    );
  }

  /**
   * Stores the upsells on the order.
   *
   * @returns {Observable<UpsellBatchStoreResponse[] | null>} the created customer tests, or null when the store failed
   */
  private storeUpsells(): Observable<UpsellBatchStoreResponse[] | null> {
    const upsells = this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice();

    return this.orderService
      .submitUpsells(upsells, this.consultationStorageService.transactionId, this.consultationStorageService.hash)
      .pipe(catchError((error) => this.handleSubmissionError(error, { upsells })));
  }

  /**
   * Stores partner email if the upsells contain partner treatment.
   *
   * @returns {Observable<ConsultationResponse>} An observable that completes when the email is stored
   */
  private storePartnerEmail(): Observable<void> {
    if (!this.treatmentConsultationQuestionnaireService.upsellsContainsPartnerTreatment()) {
      return of(null);
    }

    return this.orderService.updateOrderDetails(
      this.consultationStorageService.transactionId,
      this.consultationStorageService.hash,
      { partner_email: this.consultationStorageService.partnerEmail },
    );
  }

  /**
   * Retrieves questionnaire file attachments from IndexedDB.
   *
   * @returns {Observable<Object[]>} an observable that emits the list of questionnaire attachments with type
   */
  private getAttachmentsFromQuestionnaire(): Observable<{ file: File; type: ConsultationRequestAttachmentType }[]> {
    return this.indexedDbService.getAllItems(`${this.consultationStorageService.transactionId}-questionnaire`).pipe(
      map((questionnaireAnswers) =>
        questionnaireAnswers
          .filter((answer): answer is IndexedDbItem<File> => answer.data instanceof File)
          .map((answer) => ({
            file: answer.data,
            type:
              <ConsultationRequestAttachmentType>answer.metadata?.attachmentType ||
              ConsultationRequestAttachmentType.Others,
          })),
      ),
    );
  }

  /**
   * Handles errors that occur during the submission process.
   *
   * Logs the error with additional context and returns an observable of null.
   *
   * @param {any} error the error object
   * @param {object} context additional context to include in the error log
   *
   * @returns {Observable<null>} an observable that emits null
   */
  private handleSubmissionError(error: any, context: object = {}): Observable<null> {
    const errorContext = {
      ...context,
      transactionId: this.consultationStorageService.transactionId ?? 'Not found',
      consultationId: this.consultationStorageService.consultationId ?? 'Not found',
    };

    if (error instanceof HttpErrorResponse) {
      this.errorLoggingService.logHttpError(error, 'Consultation submission HTTP error', errorContext);
    } else {
      this.errorLoggingService.logError({
        message: 'Consultation submission error',
        ...errorContext,
        originalError: error,
      });
    }

    return of(null);
  }

  /**
   * Updates a consultation based on the treatment type and handles retries with exponential backoff.
   *
   * @returns {Observable<ConsultationResponse | null>} the updated consultation response or null for Nurse treatment
   */
  private updateConsultation(): Observable<ConsultationResponse | null> {
    const treatmentType = this.consultationStorageService.treatmentType;

    if (!this.treatmentTypeService.getTreatmentType(treatmentType)?.requiresConsultationUpdate) {
      return of(null);
    }

    return this.getTreatmentConsultationUpdatePayload(treatmentType).pipe(
      switchMap((consultationRequestPayload) =>
        this.requestRetryService.retry(
          this.consultationRequestService.update(
            this.consultationStorageService.consultationId,
            this.consultationStorageService.transactionId,
            this.consultationStorageService.hash,
            consultationRequestPayload,
          ),
          'Non-retryable error occurred during consultation update',
          {
            transactionId: this.consultationStorageService.transactionId ?? 'Not found',
            consultationId: this.consultationStorageService.consultationId ?? 'Not found',
          },
        ),
      ),
    ) as Observable<ConsultationResponse>;
  }

  /**
   * Gets the appropriate consultation request payload based on treatment type.
   *
   * @param {ConsultationTreatmentTypes} treatmentType the type of consultation treatment
   *
   * @returns {Observable<ConsultationRequestRequest>} observable that emits the consultation request payload
   */
  private getTreatmentConsultationUpdatePayload(
    treatmentType: ConsultationTreatmentTypes,
  ): Observable<ConsultationRequestRequest> {
    return treatmentType === ConsultationTreatmentTypes.Std
      ? of(this.consultationRequestService.getUpdateStdConsultationRequestPayload())
      : from(this.treatmentConsultationQuestionnaireService.getConsultationRequestPayload());
  }

  /**
   * Updates the customer's address on the order.
   * If no address is found in local storage, it returns an observable of `null`.
   *
   * @returns {Observable<any> } An observable that completes when the address is updated, or of(null) if no address to
   *                             update or error.
   */
  private updateCustomerAddress(): Observable<any> {
    if (!this.consultationStorageService.address) {
      return of(null);
    }

    return this.orderService.updateAddress(
      this.consultationStorageService.address,
      this.consultationStorageService.transactionId,
      this.consultationStorageService.hash,
    );
  }

  /**
   * Updates the lab on the order.
   * If no lab is found in local storage, it returns an observable of `null`.
   *
   * @returns {Observable<any> } An observable that completes when the lab is updated, or of(null) if no lab to update
   *                             or error.
   */
  private updateLab(): Observable<any> {
    const lab = this.consultationStorageService.lab;

    if (!lab) {
      return of(null);
    }

    return this.orderService.updateLab(
      lab.id,
      this.consultationStorageService.hash,
      this.consultationStorageService.transactionId,
    );
  }

  /**
   * Schedules the in-home collection appointment if one was booked.
   * If no booking result is found in storage, returns an observable of null.
   *
   * @returns {Observable<any>} An observable that completes when the appointment is scheduled, or of(null) if no IHC.
   */
  private scheduleInHomeCollectionAppointment(): Observable<any> {
    const bookingResult = this.storage.inHomeBookingResult;

    if (!bookingResult) {
      return of(null);
    }

    const inHomeCollection = OrderService.inHomeCollectionFromInHomeBookingResult(bookingResult);

    return this.orderService.storeInHomeCollectionAppointment(
      this.consultationStorageService.transactionId,
      this.consultationStorageService.hash,
      inHomeCollection,
    );
  }

  /**
   * Updates the preferred treatment test on the order.
   *
   * This method checks if there is a change in the treatment preferences stored in local storage from the one that
   * was previously ordered. If no change is detected, it returns an observable of `null`.
   * Otherwise, it finds the preferred treatment test to be removed and the preferred treatment test to be added (from
   * the list of available treatment preference tests), then sequentially:
   *  1. Adds the new preferred test to the order.
   *  2. Removes the previously selected customer test from the order.
   *
   * @returns {Observable<any>} An observable that completes when the treatment preference is updated, or of(null) if
   *                            no change or error.
   */
  private updatePreferredTreatment(): Observable<any> {
    const orderedTreatmentPreferences = this.consultationRequestService.getOrderedTreatmentPreferences();

    if (!orderedTreatmentPreferences || !this.consultationRequestService.hasTreatmentPreferenceChanged()) {
      return of(null);
    }

    const testToRemove: OrderTest | undefined = this.consultationStorageService.orderedTests.find(
      (test) => test.customer_tests_name === orderedTreatmentPreferences,
    );
    const testToAdd: Test | undefined = this.consultationStorageService.treatmentPreferenceTests.find(
      (test) => test.name === this.consultationStorageService.treatmentPreferences,
    );

    if (!testToRemove || !testToAdd) {
      this.errorLoggingService.logError('Error updating preferred treatment: Could not find tests to add or remove.');
      return of(null);
    }

    return this.orderService
      .addTestToOrder(testToAdd.id, this.consultationStorageService.transactionId, this.consultationStorageService.hash)
      .pipe(
        switchMap(() =>
          this.orderService.removeTestFromOrder(
            testToRemove.customer_tests_id,
            this.consultationStorageService.transactionId,
            this.consultationStorageService.hash,
          ),
        ),
      );
  }

  /**
   * Uploads questionnaire attachments from IndexedDB.
   *
   * @returns {Observable<any[]>} an observable that completes when all attachments are uploaded
   */
  private uploadAttachments(): Observable<any[]> {
    return this.getAttachmentsFromQuestionnaire().pipe(
      switchMap((attachments) =>
        attachments.length > 0
          ? forkJoin(attachments.map((attachment) => this.createAttachmentObservable(attachment)))
          : of([]),
      ),
    );
  }

  /**
   * Calculates the total amount to be submitted, including upsell prices and any remaining balance.
   *
   * @returns {number} The total amount in cents.
   */
  private getSubmitTotal(): number {
    const orderUpsells = this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice();
    const upsellChargeAmount = orderUpsells.reduce((acc, upsell) => acc + upsell.upsell_price, 0);
    const previousBalance = Math.max(this.consultationStorageService.remainingBalance, 0);

    return upsellChargeAmount + previousBalance + this.orderService.getInHomeCollectionTotal();
  }
}
